import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  buildEmbedSrcDoc,
  EMBED_HEIGHT_MESSAGE,
  EMBED_MAX_HEIGHT,
} from "../../utils/embedSrcDoc";

interface SandboxedHtmlFrameProps {
  html: string;
  title?: string;
  className?: string;
}

const INITIAL_HEIGHT = 150;

/**
 * Renders tenant HTML (scripts included) in an iframe whose height follows
 * its content, so the page scrolls instead of the frame.
 *
 * No `allow-same-origin`: a `srcdoc` frame otherwise shares this page's
 * origin, and pasted scripts could read the visitor's storage and tokens.
 */
const SandboxedHtmlFrame: React.FC<SandboxedHtmlFrameProps> = ({
  html,
  title = "Embedded content",
  className,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(INITIAL_HEIGHT);
  const srcDoc = useMemo(() => buildEmbedSrcDoc(html), [html]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data as { type?: unknown; height?: unknown };
      if (data?.type !== EMBED_HEIGHT_MESSAGE) return;
      const next = Number(data.height);
      if (!Number.isFinite(next) || next <= 0) return;
      setHeight(Math.min(Math.ceil(next), EMBED_MAX_HEIGHT));
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <iframe
      ref={iframeRef}
      title={title}
      className={className}
      srcDoc={srcDoc}
      sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
      allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
      style={{
        width: "100%",
        height: `${height}px`,
        border: 0,
        display: "block",
      }}
    />
  );
};

export default SandboxedHtmlFrame;
